/**
 * Response Utilities
 * Framework-agnostic functions for handling API responses
 *
 * @description
 * These utilities help extract data from various API response structures.
 * Use these to prevent NG0900 errors when API returns objects instead of arrays.
 */

/**
 * Extracts an array from an API response, handling various nested structures.
 *
 * @description
 * APIs often return data in different formats:
 * - Direct array: `{ data: [...] }`
 * - Nested by name: `{ data: { items: [...] } }`
 * - Nested as result: `{ data: { result: [...] } }`
 *
 * This function handles all these cases and returns a safe array.
 *
 * @example
 * ```typescript
 * // In component
 * this.apiService.get(endpoint).subscribe({
 *   next: (res: any) => {
 *     const items = extractArray(res.data, ['items', 'result', 'brands']);
 *     this.items.set(items);
 *   },
 *   error: () => {
 *     this.items.set([]); // Always set empty array on error
 *   }
 * });
 * ```
 *
 * @param data - The data from API response (could be array or object)
 * @param possibleKeys - Array of possible keys where the array might be nested
 * @returns Array of items, or empty array if not found
 */
export function extractArray<T = any>(
  data: any,
  possibleKeys: string[] = ['items', 'result', 'data', 'list', 'records']
): T[] {
  // Case 1: Data is already an array
  if (Array.isArray(data)) {
    return data;
  }

  // Case 2: Data is null/undefined
  if (!data || typeof data !== 'object') {
    return [];
  }

  // Case 3: Try to find array in nested keys
  for (const key of possibleKeys) {
    if (Array.isArray(data[key])) {
      return data[key];
    }
  }

  // Case 4: Last resort - check all object values for arrays
  const values = Object.values(data);
  for (const value of values) {
    if (Array.isArray(value)) {
      console.warn(
        'extractArray: Found array in unexpected key. Consider adding the key to possibleKeys.'
      );
      return value as T[];
    }
  }

  // No array found
  console.warn('extractArray: No array found in response. Data structure:', data);
  return [];
}

/**
 * Safely extracts array from HTTP response with headers
 *
 * @example
 * ```typescript
 * this.apiService.getHeaders(endpoint).subscribe({
 *   next: (res: any) => {
 *     const { items, totalCount } = extractArrayFromHttpResponse(
 *       res,
 *       ['brands', 'items'],
 *       'X-Pagination-Total-Count'
 *     );
 *     this.items.set(items);
 *     this.totalCount = totalCount;
 *   }
 * });
 * ```
 */
export function extractArrayFromHttpResponse<T = any>(
  response: any,
  possibleKeys: string[] = ['items', 'result', 'data'],
  totalCountHeader?: string
): { items: T[]; totalCount: number } {
  // Extract data from body or direct response
  const data = response?.body?.data || response?.body || response?.data || response;
  const items = extractArray<T>(data, possibleKeys);

  // Extract total count from headers if available
  let totalCount = items.length;
  if (totalCountHeader && response?.headers?.get) {
    const headerValue = response.headers.get(totalCountHeader);
    if (headerValue) {
      totalCount = parseInt(headerValue, 10) || items.length;
    }
  }

  return { items, totalCount };
}

/**
 * Type guard to check if a value is an array
 */
export function isArray<T>(value: any): value is T[] {
  return Array.isArray(value);
}
